// G-F2 (R-147) / R-346: the ONE global search surface — records AND pages, in the
// ⌘K palette.
//
// There is no cross-record search endpoint server-side, so the record half is a
// best-effort CLIENT-SIDE scan over the react-query caches already loaded this
// session (incidents / changes / work-orders / assets / transfer-orders /
// problems / KB / procurement) — matching by number, asset tag or title/name and
// deep-linking to the record.
// ponytail: client-side over loaded lists; a server search endpoint would scale better.
//
// ⚠ R-346 REPLACED the topbar's own dropdown with this. Two search affordances had
// grown up side by side — an inline box that found records and a sidebar filter
// that found pages — and neither could answer the other's question, so the operator
// had to already know which kind of thing they were looking for before choosing
// where to type it. `classify()` below is unchanged and is still the single owner
// of "which record number deep-links where".
import { useMemo, useState } from 'preact/hooks';
import { useQueryClient } from '@xantory/api';
import { CommandPalette, type CommandRecord, type NavSection } from '@xantory/ui';
import { useT } from '@xantory/i18n';
import { ROUTES, href } from '../routes';

interface Hit {
  key: string;
  number: string;
  title: string;
  type: string;
  href: string;
}

/** Map a record (by number prefix or asset_tag) to its type label + deep link. */
function classify(rec: Record<string, unknown>): Omit<Hit, 'title'> | null {
  const id = typeof rec.id === 'string' ? rec.id : null;
  const tag = typeof rec.asset_tag === 'string' ? rec.asset_tag : null;
  if (tag && id) return { key: id, number: tag, type: 'asset', href: href(ROUTES.assetsRecord, { id }) };
  const number = typeof rec.number === 'string' ? rec.number : null;
  if (!number) return null;
  const p = number.split('-')[0] ?? '';
  const map: Record<string, { type: string; path: string; withId?: boolean }> = {
    INC: { type: 'incident', path: ROUTES.maintenanceIncidents },
    CHG: { type: 'change', path: ROUTES.maintenanceChanges, withId: true },
    CTASK: { type: 'change', path: ROUTES.maintenanceChanges, withId: true },
    WO: { type: 'workOrder', path: ROUTES.workOrders },
    TO: { type: 'transferOrder', path: ROUTES.assetsTransfers },
    PRB: { type: 'problem', path: ROUTES.problems },
    KB: { type: 'kb', path: ROUTES.knowledgeBase },
    // R-234: a PR opens its record; a PO has only a list (rows expand in place).
    PR: { type: 'requisition', path: ROUTES.procurementRequestRecord, withId: true },
    PO: { type: 'order', path: ROUTES.procurementOrders },
    REC: { type: 'receiving', path: ROUTES.procurementReceiving },
  };
  const m = map[p];
  if (!m) return null;
  return {
    key: id ?? number,
    number,
    type: m.type,
    href: m.withId && id ? href(m.path as never, { id }) : m.path,
  };
}

/** The scan itself — kept separate from any one surface so the next thing that
 *  wants record hits (a mobile search, a Storybook story) does not re-derive them. */
function useRecordHits(query: string): Hit[] {
  const qc = useQueryClient();
  return useMemo(() => {
    const term = query.trim().toLowerCase();
    if (term.length < 2) return [];
    const out: Hit[] = [];
    const seen = new Set<string>();
    for (const q of qc.getQueryCache().getAll()) {
      const data = q.state.data;
      if (!Array.isArray(data)) continue;
      for (const raw of data) {
        if (!raw || typeof raw !== 'object') continue;
        // Fleet mirror rows wrap the record under `row`.
        const rec = ('row' in raw ? (raw as { row: unknown }).row : raw) as Record<string, unknown>;
        if (!rec || typeof rec !== 'object') continue;
        const title =
          (typeof rec.title === 'string' && rec.title) || (typeof rec.name === 'string' && rec.name) || '';
        const hay = [rec.number, rec.asset_tag, title]
          .filter((v) => typeof v === 'string')
          .join(' ')
          .toLowerCase();
        if (!hay.includes(term)) continue;
        const c = classify(rec);
        if (!c) continue;
        const dedup = `${c.type}:${c.key}`;
        if (seen.has(dedup)) continue;
        seen.add(dedup);
        out.push({ ...c, title });
        if (out.length >= 40) break;
      }
    }
    return out.slice(0, 8);
  }, [query, qc]);
}

/** `⌘K` on Apple hardware, `Ctrl K` everywhere else — read once, because the
 *  label has to match the key the operator's own machine actually listens for. */
export const SEARCH_SHORTCUT_LABEL =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)
    ? '⌘K'
    : 'Ctrl K';

interface CommandMenuProps {
  open: boolean;
  onClose: () => void;
  /** The resolved, localised nav tree — the same one the sidebar renders. */
  sections: NavSection[];
  activeKey: string;
}

/** The app's search: `ui/CommandPalette` with this console's record hits fed in. */
export function CommandMenu({ open, onClose, sections, activeKey }: CommandMenuProps) {
  const t = useT('ui');
  const [query, setQuery] = useState('');
  const hits = useRecordHits(query);
  const records: CommandRecord[] = hits.map((h) => ({
    key: `${h.type}:${h.key}`,
    number: h.number,
    title: h.title,
    type: t(`searchType_${h.type}` as 'searchType_incident'),
    href: h.href,
  }));
  return (
    <CommandPalette
      open={open}
      onClose={onClose}
      sections={sections}
      activeKey={activeKey}
      records={records}
      recordsLabel={t('searchRecords')}
      onQueryChange={setQuery}
    />
  );
}
